import { LoadingButton } from '@mui/lab'
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material'
import { GreenText } from 'components'
import { DateField, TimeField } from 'components/fields'
import { FedExDeliveryTypeDto, FedExPackageTypeDto, FedExShipmentDto, fedExShipmentValidationSchema, FedExUSStateCodeDto } from 'dtos/fedEx'
import { Form, Formik } from 'formik'

interface Props {
  deliveryTypes: FedExDeliveryTypeDto[]
  initialValues: FedExShipmentDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: any) => void
  open: boolean
  packageTypes: FedExPackageTypeDto[]
  usStateCodes: FedExUSStateCodeDto[]
}

export default function FedExOutboundLabelDialog({ deliveryTypes, initialValues, isLoading, onClose, onSave, open, packageTypes, usStateCodes }: Props) {
  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
        }}
        validateOnBlur
        validateOnChange
        validationSchema={fedExShipmentValidationSchema}
      >
        {({ errors, handleBlur, handleChange, setFieldValue, touched, values }) => {
          return (
            <Form>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>FedEx Outbound Label</DialogTitle>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      disabled={isLoading}
                      disableListWrap
                      getOptionLabel={option => option.packageType}
                      onBlur={handleBlur}
                      onChange={(_e, value) => setFieldValue('packageType', value?.enumeration ?? undefined)}
                      options={packageTypes}
                      renderInput={params => <TextField {...params} error={Boolean(errors.packageType)} helperText={errors.packageType} label='Package Type' />}
                      value={packageTypes.find(packageType => packageType.enumeration === values.packageType) ?? null}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <DateField
                      disabled={isLoading}
                      error={Boolean(errors.date)}
                      helperText={errors.date}
                      label='Ship Date'
                      onChange={value => setFieldValue('date', value)}
                      value={values.date}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TimeField
                      disabled={isLoading}
                      error={Boolean(errors.time)}
                      helperText={errors.time}
                      label='Ship Time'
                      onChange={value => setFieldValue('time', value)}
                      value={values.time?.substring(0, 5)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      disabled={isLoading}
                      disableListWrap
                      getOptionLabel={option => option.serviceType}
                      onBlur={handleBlur}
                      onChange={(_e, value) => setFieldValue('deliveryType', value?.enumeration ?? undefined)}
                      options={deliveryTypes}
                      renderInput={params => (
                        <TextField {...params} error={Boolean(errors.deliveryType)} helperText={errors.deliveryType} label='Delivery Type' />
                      )}
                      value={deliveryTypes.find(option => option.enumeration === values.deliveryType) ?? null}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(touched.weightLbs && errors.weightLbs)}
                      fullWidth
                      helperText={touched.weightLbs && errors.weightLbs}
                      InputProps={{ inputProps: { min: 1 } }}
                      label='Weight (Lbs.)'
                      name='weightLbs'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.weightLbs ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(touched.recipientContactPersonName && errors.recipientContactPersonName)}
                      fullWidth
                      helperText={touched.recipientContactPersonName && errors.recipientContactPersonName}
                      label='Ship To Name'
                      name='recipientContactPersonName'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.recipientContactPersonName ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(touched.recipientContactPhoneNumber && errors.recipientContactPhoneNumber)}
                      fullWidth
                      helperText={touched.recipientContactPhoneNumber && errors.recipientContactPhoneNumber}
                      label='Ship To Phone #'
                      name='recipientContactPhoneNumber'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.recipientContactPhoneNumber ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(touched.recipientAddressStreetLine1 && errors.recipientAddressStreetLine1)}
                      fullWidth
                      helperText={touched.recipientAddressStreetLine1 && errors.recipientAddressStreetLine1}
                      label='Ship To Address'
                      name='recipientAddressStreetLine1'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.recipientAddressStreetLine1 ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(touched.recipientAddressCity && errors.recipientAddressCity)}
                      fullWidth
                      helperText={touched.recipientAddressCity && errors.recipientAddressCity}
                      label='Ship To City'
                      name='recipientAddressCity'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.recipientAddressCity ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      disabled={isLoading}
                      disableListWrap
                      getOptionLabel={option => option.state}
                      onBlur={handleBlur}
                      onChange={(_e, value) => setFieldValue('recipientAddressStateOrProvinceCode', value?.code ?? undefined)}
                      options={usStateCodes}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(errors.recipientAddressStateOrProvinceCode)}
                          helperText={errors.recipientAddressStateOrProvinceCode}
                          label='Ship To State'
                        />
                      )}
                      value={usStateCodes.find(option => option.code === values.recipientAddressStateOrProvinceCode) ?? null}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(touched.recipientAddressPostalCode && errors.recipientAddressPostalCode)}
                      fullWidth
                      helperText={touched.recipientAddressPostalCode && errors.recipientAddressPostalCode}
                      label='Ship To Postal Code'
                      name='recipientAddressPostalCode'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.recipientAddressPostalCode ?? ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Package Type:**

Defaults to “FedEx Envelope”.

Dropdown options are hardcoded from FedEx and include:

   - FedEx Envelope
   - FedEx Extra Large Box
   - FedEx Large Box
   - FedEx Medium Box
   - FedEx Pak
   - FedEx Small Box
   - FedEx Tube

This field is required to save.

**Ship Date:**

Defaults to current day +1.

Users cannot select a past date.

This field is required to save.

**Ship Time:**

Defaults to 5:00 PM.

Users cannot select a time in the past.

This field is required to save.

**Delivery Type:**

Defaults to “FedEx Standard Overnight”.

Dropdown options are hardcoded from FedEx and include:

   - FedEx First Overnight
   - FedEx Priority Overnight
   - FedEx Standard Overnight
   - FedEx 2Day AM
   - FedEx 2Day
   - FedEx Express Saver

This field is required to save.

**Weight:**

Weight defaults to “1”.

This field is required to save.

**Ship To Fields:**

The “Ship To” fields are textboxes with a maximum of 250 characters each.

All fields is required to save.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes.

**SAVE Button:**

Clicking the SAVE button will save any changes.`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
